// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	VAULT_ENVELOPE_PREFIX,
	VAULT_HIDDEN_INVITE_CONTENT,
	VAULT_PBKDF2_ITERATIONS,
	VAULT_SALT,
	VAULT_TEMP_INVITE_CONTENT,
} from '@app/features/vault/constants';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function bytesToBase64Url(bytes: Uint8Array): string {
	let binary = '';
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
	const padded = value.replaceAll('-', '+').replaceAll('_', '/');
	const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

export async function deriveVaultKey(password: string): Promise<CryptoKey> {
	const material = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveKey']);
	return crypto.subtle.deriveKey(
		{
			name: 'PBKDF2',
			salt: encoder.encode(VAULT_SALT),
			iterations: VAULT_PBKDF2_ITERATIONS,
			hash: 'SHA-256',
		},
		material,
		{name: 'AES-GCM', length: 256},
		false,
		['encrypt', 'decrypt'],
	);
}

export async function encryptVaultText(key: CryptoKey, plaintext: string, expiryMs: number): Promise<string> {
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const ciphertext = await crypto.subtle.encrypt({name: 'AES-GCM', iv}, key, encoder.encode(plaintext));
	return `${VAULT_ENVELOPE_PREFIX}${bytesToBase64Url(iv)}.${bytesToBase64Url(new Uint8Array(ciphertext))}.${expiryMs}`;
}

export async function decryptVaultText(key: CryptoKey, envelope: string): Promise<string> {
	if (!isVaultEnvelope(envelope)) {
		throw new Error('Not a vault envelope');
	}
	const body = envelope.slice(VAULT_ENVELOPE_PREFIX.length);
	const [ivPart, ciphertextPart] = body.split('.');
	if (!ivPart || !ciphertextPart) {
		throw new Error('Malformed vault envelope');
	}
	const iv = base64UrlToBytes(ivPart);
	const ciphertext = base64UrlToBytes(ciphertextPart);
	const plaintext = await crypto.subtle.decrypt({name: 'AES-GCM', iv}, key, ciphertext);
	return decoder.decode(plaintext);
}

export function isVaultEnvelope(content: string | null | undefined): boolean {
	return typeof content === 'string' && content.startsWith(VAULT_ENVELOPE_PREFIX);
}

export function isVaultTempInvite(content: string | null | undefined): boolean {
	return typeof content === 'string' && content.trim() === VAULT_TEMP_INVITE_CONTENT;
}

export function isVaultHiddenInvite(content: string | null | undefined): boolean {
	return typeof content === 'string' && content.trim() === VAULT_HIDDEN_INVITE_CONTENT;
}

export function isVaultInvite(content: string | null | undefined): boolean {
	return isVaultTempInvite(content) || isVaultHiddenInvite(content);
}

export function getVaultEnvelopeExpiry(content: string): number | null {
	if (!isVaultEnvelope(content)) return null;
	const body = content.slice(VAULT_ENVELOPE_PREFIX.length);
	const expiryPart = body.split('.')[2];
	if (expiryPart === undefined) return null;
	const expiry = Number(expiryPart);
	if (!Number.isFinite(expiry) || expiry <= 0) return null;
	return expiry;
}

const plaintextCache = new Map<string, string>();

export function cacheVaultPlaintext(envelope: string, plaintext: string): void {
	plaintextCache.set(envelope, plaintext);
	while (plaintextCache.size > 500) {
		const oldestKey = plaintextCache.keys().next().value;
		if (oldestKey === undefined) break;
		plaintextCache.delete(oldestKey);
	}
}

export function getCachedVaultPlaintext(envelope: string): string | null {
	return plaintextCache.get(envelope) ?? null;
}
